"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { FormProvider } from "@/integrations/forms/FormProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="206856" className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.label === "Plan Gesprek" ? <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="cursor-pointer p-0 h-auto font-default text-[15px] font-medium text-foreground transition-colors hover:text-ring focus:text-ring outline-none shadow-none hover:bg-transparent">
                    {item.label}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogTitle className="sr-only">Plan een Gesprek</DialogTitle>
                  <FormProvider formId="nav-plan-gesprek-desktop" sectionName="hoofdnavigatie" defaultValues={{
              name: "",
              email: "",
              message: ""
            }}>
                    <Form />
                  </FormProvider>
                </DialogContent>
              </Dialog> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="font-default text-[15px] font-medium text-foreground transition-colors hover:text-ring focus:text-ring">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-12 px-4">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-40 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-24 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-12 px-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          {item.label === "Plan Gesprek" ? <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" className="p-0 h-auto block text-2xl font-default font-medium text-foreground hover:text-ring transition-colors text-left w-full shadow-none hover:bg-transparent">
                  {item.label}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogTitle className="sr-only">Plan een Gesprek</DialogTitle>
                <FormProvider formId="nav-plan-gesprek-mobile" sectionName="hoofdnavigatie" defaultValues={{
            name: "",
            email: "",
            message: ""
          }}>
                  <Form />
                </FormProvider>
              </DialogContent>
            </Dialog> : <Link to={item.href || "#"} className="block text-2xl font-default font-medium text-foreground hover:text-ring transition-colors">
              {item.label}
            </Link>}
        </div>)}
      <div className="pt-8 mt-4 border-t border-border/50">
        <Button variant="outline" className="w-full border-ring text-ring hover:bg-ring hover:text-white font-default font-medium h-12" asChild>
          <Link to="#">Plan Gesprek</Link>
        </Button>
      </div>
    </div>;
}
export default function Navigatie() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="hoofdnavigatie" className={`bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/70 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1629897/img/image_dj1nbgq9.jpg" alt="EduMedius – midden in het onderwijs" className="h-7" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex md:items-center md:gap-8">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>

            <Button variant="outline" className="border-ring text-ring hover:bg-ring hover:text-white font-default font-medium transition-colors duration-300" asChild>
              <Link to="#">Plan Gesprek</Link>
            </Button>
          </div>

          {}
          <div className="flex items-center md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:text-ring hover:bg-transparent focus-visible:ring-ring">
                  <Menu className="h-7 w-7" />
                  <span className="sr-only">Open hoofdmenu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="dark bg-background text-foreground border-l-border w-[300px] sm:w-[400px]">
                <SheetTitle className="sr-only">Navigatie Menu</SheetTitle>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}